import React, { useState, useEffect, useRef } from 'react';
import { mediaApi, ImageAsset } from '../services/mediaApi';
import {
  XMarkIcon, MagnifyingGlassIcon, SparklesIcon,
  CloudArrowUpIcon, PhotoIcon, TrashIcon, FolderOpenIcon
} from '@heroicons/react/24/outline';

interface Props {
  open: boolean;
  query: string;
  projectId?: number;
  onClose: () => void;
  onSelect: (image: ImageAsset) => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';
const CHART_TYPES = [
  { value: '', label: '自动判断' },
  { value: 'architecture', label: '架构图' },
  { value: 'gantt', label: '甘特图' },
  { value: 'flowchart', label: '流程图' },
];

const ImageReplaceModal: React.FC<Props> = ({ open, query, projectId, onClose, onSelect }) => {
  // ── 资料库 tab ──
  const [search, setSearch] = useState(query);
  const [images, setImages] = useState<ImageAsset[]>([]);
  const [loading, setLoading] = useState(false);

  // ── AI 生成 ──
  const [chartType, setChartType] = useState('');
  const [editableQuery, setEditableQuery] = useState(query);
  const [generating, setGenerating] = useState(false);
  const [diagramStyles, setDiagramStyles] = useState<{ id: number; name: string }[]>([]);
  const [diagramStyle, setDiagramStyle] = useState('default');

  // ── 本地上传 tab ──
  const [activeTab, setActiveTab] = useState<'library' | 'upload'>('library');
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number[]>([]);
  const [uploadedImages, setUploadedImages] = useState<ImageAsset[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── 弹窗打开时重置 ──
  useEffect(() => {
    if (open) {
      setSearch(query);
      setEditableQuery(query);
      setActiveTab('library');
      setUploadFiles([]);
      setUploadedImages([]);
      setChartType('');
      setDiagramStyle('default');
      handleSearch(query);
    }
  }, [open, query]);

  // ── 加载图表风格列表（AI 生成风格选择）──
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/api/diagram-styles`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(res => (res.ok ? res.json() : []))
      .then((data: any) => setDiagramStyles(Array.isArray(data) ? data : []))
      .catch(() => setDiagramStyles([]));
  }, []);

  // ── 资料库搜索 ──
  const handleSearch = async (q: string) => {
    setLoading(true);
    try {
      const result = await mediaApi.listImages({ q, page_size: 20 });
      setImages(result.data?.items || []);
    } catch {
      setImages([]);
    }
    setLoading(false);
  };

  // ── AI 生成 ──
  const handleAiGenerate = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
     const body: any = { query: editableQuery || query, project_id: projectId || 0 };
      if (chartType) body.chart_type = chartType;
      if (diagramStyle) body.diagram_style = diagramStyle;

      const res = await fetch(`${API_BASE_URL}/api/media/images/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.image) {
        onSelect(data.image);
        onClose();
      } else {
        alert(data.error || 'AI 生成失败，请重试');
      }
    } catch (e: any) {
      alert(`AI 生成失败: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  // ── 本地上传 ──
  const handleUploadFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    console.log('[ImageReplaceModal] 文件选择事件触发, files:', files, 'count:', files?.length);
    if (files && files.length > 0) {
      const fileList = Array.from(files);
      console.log('[ImageReplaceModal] 添加文件:', fileList.map(f => f.name));
      setUploadFiles(prev => [...prev, ...fileList]);
    }
    // 重置 input 以支持重复选同一个文件
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      setUploadFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
    }
  };

  const removeUploadFile = (idx: number) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const doUpload = async () => {
    if (!uploadFiles.length) return;
    setUploading(true);
    setUploadProgress(uploadFiles.map(() => 0));
    try {
      const res = await mediaApi.upload(uploadFiles, undefined, (pct) => {
        setUploadProgress(uploadFiles.map(() => pct));
      });
      // 上传成功后，获取完整图片信息
      const ids: number[] = (res.data?.images || []).map((i: any) => i.id);
      const uploaded: ImageAsset[] = [];
      for (const id of ids) {
        try {
          const r = await mediaApi.getImage(id);
          if (r.data) uploaded.push(r.data);
        } catch {}
      }
      setUploadedImages(prev => [...prev, ...uploaded]);
      setUploadFiles([]);
      setUploadProgress([]);
    } catch (e: any) {
      alert('上传失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  // ── 格式化文件大小 ──
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="card shadow-modal rounded-xl w-[680px] max-h-[85vh] flex flex-col bg-white" onClick={e => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h3 className="text-base font-bold text-gray-800">插入图片</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded transition-colors">
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* ── Tab 栏 ── */}
        <div className="flex border-b shrink-0">
          <button
            onClick={() => setActiveTab('library')}
            className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
              activeTab === 'library'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FolderOpenIcon className="w-4 h-4" />资料库
          </button>
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
              activeTab === 'upload'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <CloudArrowUpIcon className="w-4 h-4" />本地上传
          </button>
        </div>

        {/* ── 资料库 Tab 内容 ── */}
        {activeTab === 'library' && (
          <>
            {/* AI 生成区 */}
            <div className="px-6 py-3 border-b bg-slate-50/50 shrink-0">
              <div className="mb-2">
                <label className="text-xs text-gray-500 block mb-1">当前图片描述（可修改）：</label>
                <input
                  type="text"
                  value={editableQuery}
                  onChange={e => setEditableQuery(e.target.value)}
                  placeholder="输入图片描述，AI 将据此生成图表"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div className="flex gap-2 items-center">
                <select
                  value={chartType}
                  onChange={e => setChartType(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  {CHART_TYPES.map(ct => (
                    <option key={ct.value} value={ct.value}>{ct.label}</option>
                  ))}
                </select>
                <select
                  value={diagramStyle}
                  onChange={e => setDiagramStyle(e.target.value)}
                  title="图表风格"
                  className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="default">默认风格</option>
                  <option value="random">随机风格</option>
                  {diagramStyles.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.name}</option>
                  ))}
                </select>
                <button
                  onClick={handleAiGenerate}
                  disabled={generating}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-sm rounded-lg flex items-center justify-center gap-1.5 hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 transition-all shadow-sm"
                >
                  <SparklesIcon className="w-4 h-4" />
                  {generating ? 'AI 正在生成图表...' : 'AI 生成图表'}
                </button>
              </div>
            </div>

            {/* 搜索 */}
            <div className="px-6 py-3 shrink-0">
              <div className="flex gap-2">
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSearch(search); }}
                  placeholder="搜索素材库图片..."
                  className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <button
                  onClick={() => handleSearch(search)}
                  className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg flex items-center gap-1 hover:bg-blue-600 transition-colors"
                >
                  <MagnifyingGlassIcon className="w-4 h-4" />搜索
                </button>
              </div>
            </div>

            {/* 图片网格 */}
            <div className="flex-1 overflow-y-auto px-6 pb-4">
              {loading ? (
                <div className="text-center py-12 text-sm text-gray-400">搜索中...</div>
              ) : images.length === 0 ? (
                <div className="text-center py-12 text-sm text-gray-400">
                  未找到匹配图片。请尝试其他关键词，或切换到「本地上传」上传新图片。
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {images.map(img => (
                    <div
                      key={img.id}
                      onClick={() => { onSelect(img); onClose(); }}
                      className="cursor-pointer border border-gray-200 rounded-lg p-2 hover:border-blue-400 hover:shadow-md transition-all group"
                    >
                      <img
                        src={`${API_BASE_URL}/${img.file_path}`}
                        alt={img.original_name}
                        className="w-full h-32 object-cover rounded group-hover:brightness-95 transition-all"
                      />
                      <div className="mt-1 text-xs text-slate-600 truncate font-medium">{img.original_name}</div>
                      {img.ai_description && (
                        <div className="text-[10px] text-gray-400 line-clamp-2 mt-0.5">{img.ai_description}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── 本地上传 Tab 内容 ── */}
        {activeTab === 'upload' && (
          <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
            {/* 拖拽区域 */}
            <div
              onDragOver={e => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(e); }}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
            >
              <CloudArrowUpIcon className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">拖拽图片到此处，或 <span className="text-blue-500 font-medium">点击选择文件</span></p>
              <p className="text-xs text-gray-400 mt-1">支持 JPG、PNG、WebP、SVG，单文件不超过 20MB</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={handleUploadFiles}
            />

            {/* 待上传文件列表 */}
            {uploadFiles.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 border-b">
                  待上传 ({uploadFiles.length} 个文件)
                </div>
                <div className="divide-y max-h-40 overflow-y-auto">
                  {uploadFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <PhotoIcon className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="flex-1 truncate text-gray-700">{f.name}</span>
                      <span className="text-xs text-gray-400 shrink-0">{formatSize(f.size)}</span>
                      <button
                        onClick={() => removeUploadFile(i)}
                        className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="px-3 py-2 border-t bg-gray-50/50">
                  <button
                    onClick={doUpload}
                    disabled={uploading}
                    className="w-full px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  >
                    {uploading ? '上传中...' : `上传 ${uploadFiles.length} 个文件`}
                  </button>
                  {uploading && (
                    <div className="mt-2 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-blue-500 h-full rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress[0] || 0}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 已上传的图片 */}
            {uploadedImages.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-2">上传成功 ({uploadedImages.length} 张)</div>
                <div className="grid grid-cols-3 gap-3">
                  {uploadedImages.map(img => (
                    <div
                      key={img.id}
                      onClick={() => { onSelect(img); onClose(); }}
                      className="cursor-pointer border border-green-200 rounded-lg p-2 hover:border-green-400 hover:shadow-md transition-all bg-green-50/30"
                    >
                      <img
                        src={`${API_BASE_URL}/${img.file_path}`}
                        alt={img.original_name}
                        className="w-full h-32 object-cover rounded"
                      />
                      <div className="mt-1 text-xs text-green-700 truncate font-medium">{img.original_name}</div>
                      <div className="text-[10px] text-green-500">✓ 上传成功</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageReplaceModal;
